import React from 'react';
import { Skeleton, SkeletonButton, SkeletonRegion, SkeletonStats } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';

import { STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { MobileScreen, MobileHeader, MobileCardSkeleton } from '../../mobile';
import {
  MobileSectionStrip, RailSkeleton, SetupSurface, TableSurface, WindowPicker,
  WORKBENCH_HEADER, WORKBENCH_MAIN, WORKBENCH_ROOT,
} from '../../components/common/WorkbenchSkeleton';

/**
 * «مدیریت هزینه‌ها» while it is still fetching — the page's own shape, in
 * placeholder blocks, drawn for a `view` (DESIGN.md §"Loading & skeleton").
 *
 * The router's Suspense fallback imports this EAGERLY, and the screen uses the
 * same component for its first load, so the fallback → first-load transition
 * does not jump. That is why it imports nothing from the screen: only the kit,
 * the tokens, the density scale, i18n, the mobile primitives and the shared
 * workbench geometry.
 *
 * The desktop anatomy it mirrors (Operations.web.jsx):
 *
 *   ① header     title · subtitle | window picker · primary action
 *   ② cost strip four KPI cards, no icon slot
 *   ③ rail       176px, two groups — two daily entries and three definitions
 *   ④ surface    the view's toolbar + its dense table (دفتر هزینه، قبوض), or a
 *                heading + add-form + bordered rows (the setup views — those are
 *                NOT tables, see RowSkeleton)
 *
 * Static chrome that needs no data (the page title, the view's heading) renders
 * for real so the page keeps its identity through the wait (brief rule 5);
 * everything data-dependent is a block.
 *
 * The goods views it used to draw moved to `screens/Goods/GoodsSkeleton.jsx`
 * with their page (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md). A bookmark
 * to an old `/operations?view=items` link never reaches this file: the skeleton
 * registry resolves it to the goods page BEFORE the router forwards it, so the
 * operator sees one skeleton rather than the wrong one and then the right one.
 */

/** `?view=setup` is not a real view; it lands on the first setup entry. */
// «تعریف‌ها» used to open on «تعریف مراکز هزینه»; that view is gone (centres are
// derived now), so the group's first surviving destination is the recurring one.
const VIEW_ALIASES = { setup: 'recurring', costCenters: 'recurring' };

const TABLE_VIEWS = {
  // Column widths mirror each view's `columns` definition; the first column is
  // the flexible one and gets the name-cell block.
  expenses: { columns: [130, 180, 120, 130, 120, 128, 132], rows: 8, filters: { search: 240, select: 150, chips: 3 } },
  // Column widths mirror BillsView's own `columns`; the five status chips are the
  // derived filters plus the two stored ones the toolbar offers.
  bills: { columns: [124, 180, 150, 130, 128, 168, 128, 128], rows: 8, filters: { search: 240, select: 180, chips: 5 } },
};

const SETUP_VIEWS = {
  recurring: { maxWidth: 720, form: null, rows: 2, actions: 2 },
  billTypes: { maxWidth: 860, form: [{ flex: 1 }, { width: 150 }, { width: 150 }], rows: 3, actions: 3 },
};

export function resolveSkeletonView(view) {
  const key = VIEW_ALIASES[view] || view;
  return TABLE_VIEWS[key] || SETUP_VIEWS[key] ? key : 'expenses';
}

const viewTitleKey = (view) => `operations.views.${view}`;

// ── Desktop ──────────────────────────────────────────────────────────────────

/** The object rail's footprint: دفتر هزینه + قبوض, then the three definitions. */
export function OperationsRailSkeleton() {
  return <RailSkeleton groups={[2, 3]} />;
}

export function OperationsSkeletonWeb({ view: requested = 'expenses' }) {
  const { t } = useTranslation();
  const view = resolveSkeletonView(requested);
  const label = t('common.state.loading');
  return (
    <SkeletonRegion
      label={label}
      data-testid="operations-skeleton"
      data-view={view}
      style={WORKBENCH_ROOT}
    >
      {/* ① */}
      <header style={WORKBENCH_HEADER}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.textPrimary }}>{t('nav.items.operations')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: C.textMuted }}>{t('operations.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <WindowPicker />
          {TABLE_VIEWS[view] ? <SkeletonButton width={128} /> : null}
        </div>
      </header>

      {/* ② four KPI cards, no icon slot */}
      <div style={{ padding: '14px 20px', flexShrink: 0 }}>
        <SkeletonStats count={4} icon={false} label={label} />
      </div>

      {/* ③ + ④ */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, borderTop: `1px solid ${C.border}` }}>
        <OperationsRailSkeleton />
        <main style={WORKBENCH_MAIN}>
          {TABLE_VIEWS[view]
            ? <TableSurface spec={TABLE_VIEWS[view]} label={label} />
            : <SetupSurface spec={SETUP_VIEWS[view]} title={t(viewTitleKey(view))} label={label} />}
        </main>
      </div>
    </SkeletonRegion>
  );
}

// ── Mobile ───────────────────────────────────────────────────────────────────

/**
 * The placeholder for ONE card of each mobile list, shaped like the card the
 * list renders (Operations.mobile.jsx → src/mobile/cards). Exported so the
 * screen's `MobileList`s pass the same shapes as the page skeleton draws.
 */
export const MOBILE_CARD_SKELETONS = {
  // MobileExpenseCard: title + amount on the head line, one meta line, a
  // status badge, and (for an approver) the approve/reject shelf.
  expenses: ({ actions = 0 } = {}) => <MobileCardSkeleton badge={72} metaLines={1} chips={1} actions={actions} />,
  // Bills reuse MobileExpenseCard, but with a longer meta line — period, due
  // date, cost centre and the payment identifier — and one action shelf
  // («ثبت پرداخت» / «تأیید قبض»).
  bills: ({ actions = 0 } = {}) => <MobileCardSkeleton badge={72} metaLines={2} chips={1} actions={actions} />,
};

/** `MobileList`'s `renderSkeleton` for a view: `renderOperationsCardSkeleton('bills', { actions: 1 })`. */
export const renderOperationsCardSkeleton = (view, opts) => (i) => (
  <React.Fragment key={i}>{(MOBILE_CARD_SKELETONS[view] || MOBILE_CARD_SKELETONS.expenses)(opts)}</React.Fragment>
);

const MOBILE_VIEWS = new Set(['expenses', 'bills']);

export function OperationsSkeletonMobile({ view: requested = 'expenses', actions = 0 }) {
  const { t } = useTranslation();
  const view = MOBILE_VIEWS.has(requested) ? requested : 'expenses';
  const label = t('common.state.loading');
  const card = MOBILE_CARD_SKELETONS[view];
  return (
    <SkeletonRegion
      label={label}
      data-testid="operations-skeleton"
      data-view={view}
      style={{ height: '100%' }}
    >
      <MobileScreen
        header={(
          <MobileHeader title={t('nav.items.operations')} subtitle={t('operations.mobileTitle')} sticky>
            <div className="mobile-strip" style={{ overflowX: 'hidden', paddingBlock: '0.125rem' }}>
              <WindowPicker height={44} />
            </div>
          </MobileHeader>
        )}
      >
        {/* Two sections now — دفتر هزینه and قبوض. */}
        <MobileSectionStrip widths={[64, 52]} />

        {/* ② the money rows — full width, two of them (a label and a figure each) */}
        <div style={{ padding: '0.75rem 1rem 0', display: 'grid', gap: '0.5rem' }}>
          <MobileCardSkeleton metaLines={0} badge={88} />
          <MobileCardSkeleton metaLines={0} badge={88} />
        </div>

        {/* the filter strip — both remaining views have one */}
        <div aria-hidden style={{ display: 'flex', gap: '0.5rem', paddingBlock: '0.5rem', paddingInline: '1rem', overflow: 'hidden' }}>
          {[56, 96, 80, 72].map((w, i) => <Skeleton key={i} width={w} height={44} radius={RADIUS.pill} />)}
        </div>

        <div style={{ padding: '0 1rem 1.5rem', display: 'grid', gap: `${STACK.tight}px` }}>
          {Array.from({ length: 5 }, (_, i) => <React.Fragment key={i}>{card({ actions })}</React.Fragment>)}
        </div>
      </MobileScreen>
    </SkeletonRegion>
  );
}

export default function OperationsSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <OperationsSkeletonMobile {...props} /> : <OperationsSkeletonWeb {...props} />;
}
